import * as React from "react";

import { useAuth } from "@/hooks/useAuth";
import type { AccessibilitySettings } from "@/lib/accessibility";
import { applySettingsToCSS, type CursorSize, DEFAULTS, enableTTS } from "@/lib/accessibility";
import {
  type AccessibilitySettingsDTO,
  getAccessibilitySettings,
  saveAccessibilitySettings,
} from "@/services/accessibility";

/**
 * Safely normalize backend cursor size
 */
function normalizeCursorSize(value: string): CursorSize {
  if (value === "large" || value === "xlarge") {
    return value;
  }
  return "normal";
}

/**
 * Clamp helper
 */
function clampRate(rate: number | undefined): number {
  return Math.max(0.5, Math.min(2.0, rate ?? 1.0));
}

/**
 * Map backend DTO → UI model
 */
function dtoToSettings(dto: AccessibilitySettingsDTO): AccessibilitySettings {
  return {
    ...DEFAULTS,

    fontScale: (dto.zoom_level ?? 100) / 100,

    highContrast: dto.high_contrast ?? false,
    underlineLinks: dto.underline_links ?? false,
    increasedTextSpacing: dto.text_spacing ?? false,
    reduceMotion: dto.reduce_motion ?? false,
    enhancedKeyboardNav: dto.keyboard_navigation ?? false,
    enhancedFocusIndicators: dto.focus_indicators ?? false,

    cursorSize: normalizeCursorSize(dto.cursor_size),

    ttsEnabled: dto.tts ?? false,
    speechRate: clampRate(dto.speech_rate),

    ttsOnFocus: dto.tts_on_focus ?? false,
    ttsOnHover: dto.tts_on_hover ?? false,

    screenReaderOptimization: dto.screen_reader_optimization ?? false,

    // ✅ NEW FIELD (safe fallback)
    dyslexiaFriendlyFont: dto.dyslexia_friendly_font ?? false,
  };
}

/**
 * Map UI model → backend DTO
 */
function settingsToDto(
  userId: string,
  displayName: string | undefined,
  settings: AccessibilitySettings
): AccessibilitySettingsDTO {
  return {
    user_id: userId,
    username: displayName,

    zoom_level: Math.round(settings.fontScale * 100),

    high_contrast: settings.highContrast,
    underline_links: settings.underlineLinks,
    text_spacing: settings.increasedTextSpacing,
    reduce_motion: settings.reduceMotion,

    keyboard_navigation: settings.enhancedKeyboardNav,
    focus_indicators: settings.enhancedFocusIndicators,

    cursor_size: settings.cursorSize,

    tts: settings.ttsEnabled,
    speech_rate: clampRate(settings.speechRate),

    tts_on_focus: settings.ttsOnFocus,
    tts_on_hover: settings.ttsOnHover,

    screen_reader_optimization: settings.screenReaderOptimization,

    // ✅ NEW FIELD
    dyslexia_friendly_font: settings.dyslexiaFriendlyFont,
  };
}

export function useAccessibilitySettings() {
  const { account } = useAuth();
  const userId = account?.id;

  const [settings, setSettings] = React.useState<AccessibilitySettings>(DEFAULTS);

  /**
   * Load settings from backend
   */
  const loadSettings = React.useCallback(async () => {
    if (!userId) return;

    try {
      const data = await getAccessibilitySettings(userId);

      if (data) {
        setSettings(dtoToSettings(data));
      }
    } catch {
      // ✅ Fail silently (keep UX smooth)
    }
  }, [userId]);

  React.useEffect(() => {
    if (userId) {
      loadSettings();
    }
  }, [userId, loadSettings]);

  /**
   * Save settings to backend
   */
  const saveSettings = React.useCallback(
    async (override?: AccessibilitySettings) => {
      if (!userId) return;

      const data = override ?? settings;

      try {
        await saveAccessibilitySettings(
          settingsToDto(userId, account?.displayName || account?.username, data)
        );
      } catch {
        // ✅ Do not block UI
      }
    },
    [userId, account?.displayName, account?.username, settings]
  );

  /**
   * Apply runtime accessibility behavior
   */
  React.useEffect(() => {
    applySettingsToCSS(settings);
    enableTTS(settings);
  }, [settings]);

  return {
    settings,
    setSettings,
    loadSettings,
    saveSettings,
  };
}
